export const feedbackOptions = [
  { display: '✔️', label: 'Yes', value: 'yes' },
  { display: '😵', label: 'Unsure', value: 'unsure' },
  { display: '❌', label: 'No', value: 'no' },
  { display: '👍', label: 'Thumbs up', value: 'thumbs-up' },
  { display: '👎', label: 'Thumbs down', value: 'thumbs-down' },
  { display: '→', label: 'Faster', value: 'faster' },
  { display: '←', label: 'Slower', value: 'slower' },
] as const;

export type FeedbackToken = (typeof feedbackOptions)[number]['value'];
export const feedbackTokenValues = feedbackOptions.map((option) => option.value) as readonly FeedbackToken[];
export const feedbackRatingValues = ['0', '1', '2', '3', '4', '5', '6', '7', '8', '9', '10'] as const;
export type FeedbackRatingValue = (typeof feedbackRatingValues)[number];
export type FeedbackValue = FeedbackToken | FeedbackRatingValue;

const feedbackTokenSet = new Set<string>(feedbackOptions.map((option) => option.value));
const feedbackLabelByValue = new Map<string, string>(feedbackOptions.map((option) => [option.value, option.label]));
const feedbackDisplayByValue = new Map<string, string>(feedbackOptions.map((option) => [option.value, option.display]));

export function isFeedbackRatingValue(value: string): boolean {
  return /^(?:10|[0-9])$/u.test(value.trim());
}

export function normalizeFeedbackValue(value: string | null | undefined): string {
  if (!value) {
    return '';
  }

  const trimmedValue = value.trim();

  if (trimmedValue.length === 0) {
    return '';
  }

  if (isFeedbackRatingValue(trimmedValue)) {
    return String(Number.parseInt(trimmedValue, 10));
  }

  if (feedbackTokenSet.has(trimmedValue)) {
    return trimmedValue;
  }

  return '';
}

export function formatFeedbackDisplay(value: string | null | undefined): string {
  const normalizedValue = normalizeFeedbackValue(value);

  if (!normalizedValue) {
    return '';
  }

  if (isFeedbackRatingValue(normalizedValue)) {
    return `⭐ ${normalizedValue}`;
  }

  return feedbackDisplayByValue.get(normalizedValue) ?? normalizedValue;
}

export function formatFeedbackDetail(value: string | null | undefined): string {
  const normalizedValue = normalizeFeedbackValue(value);

  if (!normalizedValue) {
    return '';
  }

  if (isFeedbackRatingValue(normalizedValue)) {
    return normalizedValue;
  }

  return feedbackLabelByValue.get(normalizedValue) ?? normalizedValue;
}
